import { useContext, useEffect, useRef, useState } from "react";
import { LangContext } from "./LangContext";
import { relativeTime, hasKorean, pickKoreanTitle, cleanText } from "../lib/utils";
import { CLS_META, DIR_COLORS, dirLabel } from "./PCNewsCard";

function SectionHeader({ children }) {
  return (
    <div style={{ padding: "22px 26px 10px" }}>
      <span style={{ fontSize: 10, textTransform: "uppercase", color: "var(--pc-fg-3)", letterSpacing: ".08em", fontWeight: 700 }}>
        {children}
      </span>
    </div>
  );
}

function Bullet({ text, muted }) {
  return (
    <div style={{
      display: "flex", gap: 8, fontSize: 13,
      color: muted ? "var(--pc-fg-3-warm)" : "var(--pc-fg-2)",
      lineHeight: 1.55, marginBottom: 3,
    }}>
      <span style={{ flexShrink: 0, color: "var(--pc-fg-4)" }}>·</span>
      <span>{text}</span>
    </div>
  );
}

function ArticleRow({ article, onClick }) {
  const lang = useContext(LangContext);
  const cm = CLS_META[article.cls];
  const t = article.publishedAt ? relativeTime(article.publishedAt) : article.time;
  const rowHeadline = lang === "en"
    ? cleanText(article.hed)
    : cleanText(pickKoreanTitle(article.hed_ko, article.hed));
  return (
    <div
      onClick={onClick}
      style={{ padding: "14px 0", cursor: onClick ? "pointer" : "default" }}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 4 }}>
        {article.dir && (
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: DIR_COLORS[article.dir] }}>
            {dirLabel(article.dir, lang)}
          </span>
        )}
        {cm && (
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: cm.color }}>
            {cm.label}
          </span>
        )}
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--pc-fg-2)" }}>{article.src}</span>
        <span style={{ fontSize: 10, color: "var(--pc-fg-3)", fontVariantNumeric: "tabular-nums" }}>· {t}</span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--pc-fg-1)", lineHeight: 1.4, letterSpacing: "-.005em" }}>
        {rowHeadline}
      </div>
    </div>
  );
}

function MetaBlock({ article }) {
  const lang = useContext(LangContext);
  const cm = CLS_META[article.cls] || CLS_META.common;
  const t = article.publishedAt ? relativeTime(article.publishedAt) : article.time;
  const headline = lang === "en"
    ? cleanText(article.hed)
    : cleanText(pickKoreanTitle(article.hed_ko, article.hed));
  const subline = lang === "en" ? null : (hasKorean(article.hed_ko) ? cleanText(article.hed) : "");
  return (
    <div style={{ padding: "24px 26px 22px", borderBottom: "1px solid var(--pc-line-3)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: DIR_COLORS[article.dir] || "var(--pc-fg-3)" }}>
          {dirLabel(article.dir, lang)}
        </span>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: cm.color }}>
          {cm.label}
        </span>
      </div>
      <div style={{ fontSize: 21, fontWeight: 700, color: "var(--pc-fg-1)", lineHeight: 1.3, letterSpacing: "-.01em", marginBottom: 8 }}>
        {headline}
      </div>
      {subline && (
        <div style={{ fontSize: 13, color: "rgba(122,119,115,.65)", lineHeight: 1.45, marginBottom: 14 }}>
          {subline}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--pc-fg-2)" }}>{article.src}</span>
        <span style={{ fontSize: 10, color: "var(--pc-fg-3)", fontVariantNumeric: "tabular-nums" }}>· {t}</span>
      </div>
    </div>
  );
}

// Find similar articles by simple token overlap (no AI).
function findOverlap(article, allArticles, max = 4) {
  const text = ((article.hed_ko || "") + " " + (article.hed || "") + " " + (article.summary || "")).toLowerCase();
  const myTokens = new Set(text.match(/[a-zㄱ-힝가-힣]{3,}/gi) || []);
  if (myTokens.size === 0) return [];

  const stop = new Set(["iran", "이란", "the", "and", "for", "with", "from", "이번", "이번에", "관련", "보도"]);
  for (const s of stop) myTokens.delete(s);

  const candidates = allArticles
    .filter(a => a.id !== article.id && a.origin === article.origin)
    .map(a => {
      const t2 = ((a.hed_ko || "") + " " + (a.hed || "") + " " + (a.summary || "")).toLowerCase();
      const tokens2 = t2.match(/[a-zㄱ-힝가-힣]{3,}/gi) || [];
      let score = 0;
      for (const tok of tokens2) if (myTokens.has(tok)) score += 1;
      return { article: a, score };
    })
    .filter(x => x.score >= 3)
    .sort((p, q) => q.score - p.score)
    .slice(0, max)
    .map(x => x.article);

  return candidates;
}

function ArticleAnalysis({ article, allArticles, adversaryArticles, onSelect }) {
  const lang = useContext(LangContext);
  const overlap = findOverlap(article, allArticles, 4);
  const advMatches = findOverlap(article, adversaryArticles, 3);

  const excerpt = cleanText((article.cls === "gun" || article.cls === "watershed") && article.summary_ko ? article.summary_ko : article.summary);

  return (
    <div className="pc-anim-fadeUp">
      {/* What it says — summary or summary_ko */}
      {excerpt && excerpt.length >= 20 && (
        <>
          <SectionHeader>WHAT IT SAYS</SectionHeader>
          <div style={{ padding: "0 26px 18px" }}>
            <Bullet text={excerpt} />
          </div>
          <div style={{ padding: "4px 26px 0" }}>
            <div style={{ borderTop: "2px solid rgba(255,255,255,.6)" }} />
          </div>
        </>
      )}

      {/* Why it matters */}
      {article.reasoning && (
        <>
          <SectionHeader>WHY IT MATTERS</SectionHeader>
          <div style={{ padding: "0 26px 18px" }}>
            <div style={{ fontSize: 13.5, color: "var(--pc-fg-1)", lineHeight: 1.6 }}>{article.reasoning}</div>
          </div>
          <div style={{ padding: "4px 26px 0" }}>
            <div style={{ borderTop: "2px solid rgba(255,255,255,.6)" }} />
          </div>
        </>
      )}

      {/* OTHER COVERAGE */}
      <SectionHeader>{lang === "en" ? "OTHER COVERAGE" : "타매체 — 같은 키워드"}</SectionHeader>
      <div style={{ padding: "0 26px 18px" }}>
        {overlap.length === 0 ? (
          <div style={{ fontSize: 12.5, color: "var(--pc-fg-3)" }}>
            {lang === "en" ? "No coverage from other outlets." : "다른 매체 송고 없음."}
          </div>
        ) : overlap.map((a, i) => (
          <div key={i} style={{ borderBottom: i < overlap.length - 1 ? "1px solid var(--pc-line-3)" : "none" }}>
            <ArticleRow article={a} onClick={() => onSelect && onSelect(a)} />
          </div>
        ))}
      </div>
      <div style={{ padding: "4px 26px 0" }}>
        <div style={{ borderTop: "2px solid rgba(255,255,255,.6)" }} />
      </div>

      {/* ADVERSARY */}
      <SectionHeader>ADVERSARY</SectionHeader>
      <div style={{ padding: "0 26px 18px" }}>
        {advMatches.length === 0 ? (
          <div style={{ fontSize: 12.5, color: "var(--pc-fg-3)" }}>
            {lang === "en" ? "No related adversary coverage." : "관련 적대국 보도 없음."}
          </div>
        ) : advMatches.map((a, i) => (
          <div key={i} style={{ borderBottom: i < advMatches.length - 1 ? "1px solid var(--pc-line-3)" : "none" }}>
            <ArticleRow article={a} onClick={() => onSelect && onSelect(a)} />
          </div>
        ))}
      </div>
      <div style={{ padding: "4px 26px 0" }}>
        <div style={{ borderTop: "2px solid rgba(255,255,255,.6)" }} />
      </div>

      {/* FOR IDEA */}
      <SectionHeader>FOR IDEA</SectionHeader>
      <div style={{ padding: "0 26px 28px" }}>
        {article.reasoning && (
          <div style={{ marginBottom: 18 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--pc-fg-3)", display: "block", marginBottom: 6 }}>
              {lang === "en" ? "FLOW" : "흐름"}
            </span>
            <div style={{ fontSize: 13.5, color: "var(--pc-fg-1)", lineHeight: 1.6 }}>{article.reasoning}</div>
          </div>
        )}
        <div style={{ marginBottom: article.url ? 18 : 0 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--pc-fg-3)", display: "block", marginBottom: 6 }}>
            {lang === "en" ? "EVIDENCE" : "근거"}
            <span style={{ marginLeft: 8, color: "var(--pc-fg-3)", letterSpacing: ".02em", textTransform: "none", fontWeight: 500 }}>
              {lang === "en" ? "— verify originals" : "— 원본 검증 필수"}
            </span>
          </span>
          <div style={{ display: "flex", gap: 8, fontSize: 13, color: "var(--pc-fg-2)", lineHeight: 1.55, marginBottom: 3 }}>
            <span style={{ flexShrink: 0, color: "var(--pc-fg-4)" }}>·</span>
            <span>{article.src}{lang === "en" ? " — primary source" : " — 단독 보도"}</span>
          </div>
          {(article.altSources || []).map((s, i) => (
            <div key={i} style={{ display: "flex", gap: 8, fontSize: 13, color: "var(--pc-fg-2)", lineHeight: 1.55, marginBottom: 3 }}>
              <span style={{ flexShrink: 0, color: "var(--pc-fg-4)" }}>·</span>
              <span>{s.src || s}{lang === "en" ? " — same event, separate filing" : " — 동일 사건 별도 보도"}</span>
            </div>
          ))}
        </div>
        {article.url && (
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: "transparent", border: "1px solid var(--pc-line-3)",
              color: "var(--pc-fg-2)", padding: "9px 14px", fontSize: 12,
              fontWeight: 600, letterSpacing: ".02em", borderRadius: 0,
              textDecoration: "none",
            }}
          >
            {lang === "en" ? "View Original" : "원문 보기"}
            <span style={{ color: "var(--pc-fg-3)" }}>→</span>
          </a>
        )}
      </div>
    </div>
  );
}

function DeskEmpty({ allArticles, adversaryArticles, onSelect }) {
  const lang = useContext(LangContext);
  const topGun = allArticles.find(a => a.cls === "gun") || allArticles.find(a => a.cls === "watershed");
  const topAdv = adversaryArticles[0];

  return (
    <div>
      <div style={{ padding: "26px 26px 22px", borderBottom: "1px solid var(--pc-line-3)" }}>
        <span style={{ fontSize: 10, textTransform: "uppercase", color: "var(--pc-fg-3)", letterSpacing: ".08em", fontWeight: 700, display: "block", marginBottom: 10 }}>
          {lang === "en" ? "TODAY'S DESK" : "오늘의 데스크"}
        </span>
        <div style={{ fontSize: 14, color: "var(--pc-fg-2)", lineHeight: 1.6 }}>
          {lang === "en"
            ? "Select an article to see cross-coverage, adversary media, and reporting angles."
            : "좌측에서 기사를 선택하면 본문 발췌, AI 해석, 타매체·적대국 미디어 매칭이 표시됩니다."}
        </div>
      </div>

      {topGun && (
        <>
          <SectionHeader>{lang === "en" ? "Latest Smoking Gun" : "가장 최근 Smoking Gun"}</SectionHeader>
          <div style={{ padding: "0 26px 18px" }}>
            <ArticleRow article={topGun} onClick={() => onSelect(topGun)} />
          </div>
          <div style={{ padding: "4px 26px 0" }}>
            <div style={{ borderTop: "2px solid rgba(255,255,255,.6)" }} />
          </div>
        </>
      )}

      {topAdv && (
        <>
          <SectionHeader>{lang === "en" ? "Adversary Media — Latest" : "적대국 미디어 — 최근"}</SectionHeader>
          <div style={{ padding: "0 26px 26px" }}>
            <ArticleRow article={topAdv} onClick={() => onSelect(topAdv)} />
          </div>
        </>
      )}
    </div>
  );
}

export default function PCRightPanel({ article, allArticles, adversaryArticles, onSelect }) {
  const [width, setWidth] = useState(() => {
    const saved = parseInt(localStorage.getItem("de-pc-right-width") || "", 10);
    return Number.isFinite(saved) && saved >= 360 && saved <= 900 ? saved : 480;
  });
  const draggingRef = useRef(false);

  const onMouseDown = (e) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const w = Math.min(900, Math.max(360, window.innerWidth - e.clientX));
      setWidth(w);
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      try { localStorage.setItem("de-pc-right-width", String(width)); } catch {}
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [width]);

  return (
    <aside style={{
      width, flexShrink: 0,
      borderLeft: "1px solid var(--pc-line-3)",
      background: "var(--pc-bg-2)",
      overflowY: "auto",
      position: "relative",
    }}>
      <div
        onMouseDown={onMouseDown}
        title="드래그해서 크기 조절"
        style={{
          position: "absolute", left: -3, top: 0, bottom: 0, width: 6,
          cursor: "col-resize", zIndex: 10, background: "transparent",
        }}
        onMouseEnter={e => (e.currentTarget.style.background = "rgba(255,255,255,.08)")}
        onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
      />
      {article ? (
        <>
          <MetaBlock article={article} />
          <ArticleAnalysis article={article} allArticles={allArticles} adversaryArticles={adversaryArticles} onSelect={onSelect} />
        </>
      ) : (
        <DeskEmpty allArticles={allArticles} adversaryArticles={adversaryArticles} onSelect={onSelect} />
      )}
    </aside>
  );
}
